/*
 * WaWiApp.tsx - Warenwirtschaftliche Funktionen
 *
 * Created by Dr. Maximillian Dornseif 2020-04-18
 * Copyright (c) 2020-2022 Maximillian Dornseif / HUDORA GmbH
 * Based on huWaWi2 (c) 2011-2021, 2023 Maximillian Dornseif / HUDORA GmbH
 */

import { ISidebarProps, WithSidebar } from 'fluentui-hooks'
import React from 'react'

import { IcConfigApp } from '../Ic/IcConfigApp'
import { KindListePaginated3 } from '../lib/components/ke/KindListePaginated3'
import { KindPage3 } from '../lib/components/ke/KindPage3'
import { KindPagePaginated3 } from '../lib/components/ke/KindPagePaginated3'
import { VA_EDIT_MUTATION } from '../Stammdaten/StammKundenAppQueries'
import { WaWiIcApp } from './Auftraege/WaWiAuftragApp'
import { EntwurfApp } from './Entwuerfe/WaWiEntwurfEditor'
import { WaWiEntwurfErfassen } from './Entwuerfe/WaWiEntwurfErfassen'
import { WaWiReservierungenApp } from './Reservierungen/WaWiReservierungenApp3'
import { WaWiReservierungErfassen } from './Reservierungen/WaWiReservierungErfassen'
import {
  AUFTRAGSENTWURFALLE_LIST_QUERY as AUFTRAGSENTWURF_ALLE_LIST_QUERY,
  AUFTRAG_GESPERRT_LIST_QUERY,
  LAGERLISTE_QUERY,
  LIST_UNGENEHMIGT_QUERY,
  PREIS_CREATE_MUTATION,
  PREIS_EDIT_MUTATION,
  PREIS_LIST_QUERY,
  QX_EDIT_MUTATION,
  QX_ENTITY_QUERY,
  QX_LIST_QUERY,
  RESERVIERUNGSPOS_LIST_QUERY,
  TEMPLATECONFIG_EDIT_MUTATION,
  TEMPLATECONFIG_ENTITY_QUERY,
  TEMPLATECONFIG_LIST_QUERY,
  VA_ENTITY_QUERY,
  VA_LIST_QUERY,
} from './WaWiAppQueries'
import { WaWiGruppenApp } from './WaWiGruppenApp'
import { WaWiItemsApp } from './WaWiItemsApp'
import { WaWiKundenApp } from './WaWiKundenApp'
import { WaWiMonitoringApp } from './WaWiMonitoringApp'
import { WaWiProdukteApp } from './WaWiProdukteApp'

export const WaWiApp = (props) => {
  const sidebar: ISidebarProps = {
    Aufträge: {
      links: [
        { title: 'Neu erfassen', path: '/wawi/ic/aneu', component: <WaWiEntwurfErfassen /> },
        {
          title: 'Entwürfe',
          path: '/wawi/ic/e',
          component: <EntwurfApp></EntwurfApp>,
        },
        {
          title: '- erledigt',
          path: '/wawi/ic/d',
          component: (
            <KindPage3
              basePath={'/wawi/ic'}
              componentPath={'d'}
              schemaName={'H3AuftragsEntwurf'} // commandBarItems={nearBarItems}
              title={'Erledigte Auftrags-Entwürfe'}
              listQuery={AUFTRAGSENTWURF_ALLE_LIST_QUERY}
              hideExcept={[
                'designator',
                'kundennr',
                'keineWarnungen',
                'valide',
                'warnings',
                'created_via',
                'anlieferdatum',
                'lager',
                'auftragsnr_kunde',
                'created_at',
                'transportId',
                'erfasst_von',
                'errors',
              ]}
            />
          ),
        },
        { title: 'offen', path: '/wawi/ic/a', component: <WaWiIcApp /> },
        {
          title: '- freigeben',
          path: '/wawi/ic/genehmigungen',
          component: (
            <KindListePaginated3
              title={'Aufträge zur Genehmigung'}
              schemaName={'HwAuftrag'}
              listQuery={LIST_UNGENEHMIGT_QUERY}
              hideExcept={[
                'auftragsnr',
                'empfaengernr',
                'kunde.name',
                'anlieferdatum_von',
                'anlieferdatum_bis',
                'verbandsnr',
                'erfasst_von',
              ]}
            />
          ),
        },
        {
          title: '- gesperrt',
          path: '/wawi/ic/gesperrt_a',
          component: (
            <KindPage3
              basePath={'/wawi/ic'}
              componentPath={'gesperrt_a'}
              title={'Gesperrte Aufträge'}
              schemaName={'HwAuftrag'}
              listQuery={AUFTRAG_GESPERRT_LIST_QUERY}
              hideExcept={[
                'auftragsnr',
                'empfaengernr',
                'kunde.name',
                'auftragsnr_kunde',
                'anlieferdatum_von',
                'anlieferdatum_bis',
                'status',
                'zuteilungssperre',
                'created_via',
              ]}
            />
          ),
        },
      ],
    },
    Blockaufträge: {
      links: [
        { title: 'neu erfassen', path: '/wawi/ic/rneu', component: <WaWiReservierungErfassen /> },
        { title: 'offen', path: '/wawi/ic/rk', component: <WaWiReservierungenApp /> },
        {
          title: 'Positionen',
          path: '/wawi/ic/rp',
          component: (
            <KindListePaginated3
              title={'Resevierungspositionen'}
              schemaName={'HwReservierungsPosition'}
              hideExcept={[
                'reservierungsnr',
                'bartnr',
                'empfaengernr',
                'name',
                'anlieferdatum_von',
                'anlieferdatum_bis',
                'menge_offen',
              ]}
              listQuery={RESERVIERUNGSPOS_LIST_QUERY}
            />
          ),
        },
      ],
    },
    Recherche: {
      links: [
        { title: 'Kunden', path: '/wawi/k', component: <WaWiKundenApp /> },
        { title: 'Gruppen', path: '/wawi/g', component: <WaWiGruppenApp /> },
        {
          title: 'Versandanweisungen',
          path: '/wawi/va',
          component: (
            <KindPagePaginated3
              basePath={'/wawi'}
              componentPath={'va'}
              title={'Versandanweisungen'}
              schemaName={'HwVersandanweisung'}
              listQuery={VA_LIST_QUERY}
              entityQuery={VA_ENTITY_QUERY}
              mutation={VA_EDIT_MUTATION}
            />
          ),
        },
      ],
    },
    Artikel: {
      links: [
        { title: 'Produkte', path: '/wawi/prod', component: <WaWiProdukteApp /> },
        { title: 'Items', path: '/wawi/item', component: <WaWiItemsApp /> },
        {
          title: '- Verfügbar',
          path: '/wawi/lagerliste',
          component: (
            <KindListePaginated3
              title={'Verfügbar am Lager'}
              schemaName={'H3Item'}
              hideExcept={[
                'designator',
                'name',
                'zukunft.freie_menge',
                'zukunft.frei_ab',
                'zukunft.naechster_zulauf',
                'zukunft.zukuenftiger_zulauf',
                'zukunft.frei_max',
                'preisResolved.fh',
                'preisResolved.uvp',
                'skuSum.mngEx',
                'skuSum.mngPal',
              ]}
              listQuery={LAGERLISTE_QUERY}
            />
          ),
        },
        {
          title: 'Preise',
          path: '/wawi/preise',
          component: (
            <div className="authEnforced">
              <KindListePaginated3
                title={'Preise'}
                schemaName={'HwPreis'}
                listQuery={PREIS_LIST_QUERY}
                createMutation={PREIS_CREATE_MUTATION}
                mutation={PREIS_EDIT_MUTATION}
                rowToolsEnabled={true}
              />
            </div>
          ),
        },
      ],
    },
    System: {
      collapseByDefault: true,
      links: [
        {
          title: 'QX Preise',
          path: '/wawi/qx',
          component: (
            <KindPage3
              basePath={'/wawi'}
              componentPath={'qx'}
              schemaName={'HwIcQxPreis'}
              title={'Preise für Zusatzleistungen'}
              listQuery={QX_LIST_QUERY}
              entityQuery={QX_ENTITY_QUERY}
              mutation={QX_EDIT_MUTATION}
            />
          ),
        },
        {
          title: 'Templates',
          path: '/wawi/configTemplates',
          component: (
            <div className="authEnforced">
              <KindPage3
                basePath="/wawi"
                componentPath={'configTemplates'}
                schemaName={'H3ConfigTemplate'}
                title={'Carbone Beleg-Template Konfiguration'}
                listQuery={TEMPLATECONFIG_LIST_QUERY}
                entityQuery={TEMPLATECONFIG_ENTITY_QUERY}
                mutation={TEMPLATECONFIG_EDIT_MUTATION}
              />
            </div>
          ),
        },
        {
          title: 'Konfiguration',
          path: '/ic/config/SYSCONF1',
          component: <IcConfigApp />,
        },
        {
          title: 'Monitoring',
          path: '/wawi/monitoring',
          component: <WaWiMonitoringApp />,
        },
      ],
    },
  }

  return (
    <React.StrictMode>
      <WithSidebar items={sidebar} />
    </React.StrictMode>
  )
}
